/**
 * Minimal promise wrapper over IndexedDB. Two object stores:
 *  - `tables`: one record per domain table (key = table name, value = row[])
 *  - `blobs`:  binary payloads (recording chunks, assembled audio, portraits)
 *
 * Kept deliberately tiny so the prototype has no runtime dependencies; the
 * production build keeps using it for local drafts and offline queues.
 */
const DB_NAME = 'edry-family-prototype';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

export function idbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined';
  } catch {
    return false;
  }
}

/**
 * Open (or reuse) the connection. A failed open is *not* remembered — the next
 * call tries again — and a connection the browser drops (`onclose`, or another
 * tab asking for a version change) is forgotten so the next call reopens it.
 * Before this, one transient failure at start-up left the whole session
 * reading nothing from storage: every photo silently "disappeared" until the
 * page was reloaded.
 */
function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  const attempt = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('tables')) db.createObjectStore('tables');
      if (!db.objectStoreNames.contains('blobs')) db.createObjectStore('blobs');
    };
    req.onsuccess = () => {
      const db = req.result;
      const forget = () => {
        db.close();
        if (dbPromise === attempt) dbPromise = null;
      };
      db.onversionchange = forget;
      db.onclose = () => {
        if (dbPromise === attempt) dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    // "blocked" only means another tab still holds an older connection; the open usually
    // completes once it lets go, so keep waiting rather than failing.
    req.onblocked = () => console.warn('IndexedDB open is waiting for another tab');
  });
  dbPromise = attempt;
  attempt.catch(() => {
    if (dbPromise === attempt) dbPromise = null;
  });
  return attempt;
}

/** Run an IndexedDB operation, reopening the connection and trying once more if it was stale. */
async function withDb<T>(fn: (db: IDBDatabase) => Promise<T>): Promise<T> {
  try {
    return await fn(await open());
  } catch (e) {
    // A closed connection throws InvalidStateError; a transient failure is worth one retry too.
    if (dbPromise) {
      try {
        (await dbPromise).close();
      } catch {
        /* ignore */
      }
      dbPromise = null;
    }
    await new Promise((r) => setTimeout(r, 150));
    try {
      return await fn(await open());
    } catch {
      throw e;
    }
  }
}

function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export function idbGet<T>(store: 'tables' | 'blobs', key: string): Promise<T | undefined> {
  return withDb((db) => {
    const tx = db.transaction(store, 'readonly');
    return wrap(tx.objectStore(store).get(key)) as Promise<T | undefined>;
  });
}

export function idbSet(store: 'tables' | 'blobs', key: string, value: unknown): Promise<void> {
  return withDb((db) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(value, key);
    return done(tx);
  });
}

export function idbSetMany(store: 'tables' | 'blobs', entries: Array<[string, unknown]>): Promise<void> {
  if (!entries.length) return Promise.resolve();
  return withDb((db) => {
    const tx = db.transaction(store, 'readwrite');
    const os = tx.objectStore(store);
    for (const [k, v] of entries) os.put(v, k);
    return done(tx);
  });
}

export function idbDelete(store: 'tables' | 'blobs', key: string): Promise<void> {
  return withDb((db) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).delete(key);
    return done(tx);
  });
}

export function idbKeys(store: 'tables' | 'blobs'): Promise<string[]> {
  return withDb(async (db) => {
    const tx = db.transaction(store, 'readonly');
    const keys = await wrap(tx.objectStore(store).getAllKeys());
    return keys.map(String);
  });
}

export function idbClearAll(): Promise<void> {
  return withDb((db) => {
    const tx = db.transaction(['tables', 'blobs'], 'readwrite');
    tx.objectStore('tables').clear();
    tx.objectStore('blobs').clear();
    return done(tx);
  });
}
